import { spawn, ChildProcess } from 'node:child_process';
import { createInterface, Interface } from 'node:readline';
import { Interceptor, ToolCallEvent } from './interceptor.js';
import { LocalToolsHandler, LOCAL_TOOL_DEFINITIONS } from './local-tools.js';
import { metrics } from '../metrics.js';

export interface ProxyServerOptions {
  targetCommand: string;
  localTools?: LocalToolsHandler;
}

/**
 * Bidirectional JSON-RPC message forwarder.
 *
 * Spawns the target MCP server as a child process (stdio transport),
 * forwards NDJSON messages in both directions, and uses an Interceptor
 * to snoop on tools/call requests and responses.
 *
 * If localTools is provided, browserforge_* tool calls are handled locally
 * (not forwarded to the child), and tools/list responses are augmented with
 * the local tool definitions.
 */
export class ProxyServer {
  private readonly targetCommand: string;
  private readonly interceptor = new Interceptor();
  private readonly localTools?: LocalToolsHandler;
  private readonly pendingToolsListIds = new Set<string | number>();
  private child: ChildProcess | undefined;
  private childRl: Interface | undefined;
  private outputHandlers: Array<(line: string) => void> = [];
  private _isRunning = false;

  constructor(options: ProxyServerOptions) {
    this.targetCommand = options.targetCommand;
    this.localTools = options.localTools;
  }

  get isRunning(): boolean {
    return this._isRunning;
  }

  /** Register a handler for output lines (responses from the target MCP server). */
  onOutput(handler: (line: string) => void): void {
    this.outputHandlers.push(handler);
  }

  /** Register a handler for intercepted tool call events. */
  onToolCall(handler: (event: ToolCallEvent) => void): void {
    this.interceptor.on('tool-call', handler);
  }

  /**
   * Send a JSON-RPC message to the target MCP server.
   *
   * Local browserforge_* tools are handled here and never forwarded to the child.
   * tools/list requests are tracked so their responses can be augmented.
   */
  send(msg: Record<string, unknown>): void {
    // ── Local tool interception ──────────────────────────────────────────────
    if (this.localTools && msg['method'] === 'tools/call') {
      const params = msg['params'] as Record<string, unknown> | undefined;
      const toolName = params?.['name'] as string | undefined;

      if (toolName && this.localTools.isLocal(toolName)) {
        const id = msg['id'];
        const args = (params?.['arguments'] as Record<string, unknown>) ?? {};
        const result = this.localTools.handle(toolName, args);
        const response = JSON.stringify({ jsonrpc: '2.0', id, result });
        for (const handler of this.outputHandlers) {
          handler(response);
        }
        return; // Do NOT forward to child
      }
    }

    if (!this.child?.stdin?.writable) return;

    const line = JSON.stringify(msg);

    // Track tools/list requests so we can inject local tools into the response
    if (this.localTools && msg['method'] === 'tools/list' && msg['id'] != null) {
      this.pendingToolsListIds.add(msg['id'] as string | number);
    }

    // Let the interceptor inspect outbound requests
    this.interceptor.onRequest(msg);

    metrics.increment('proxyMessagesForwarded');
    this.child.stdin.write(line + '\n');
  }

  /** Spawn the child process and start the bidirectional bridge. */
  async start(): Promise<void> {
    const parts = this.targetCommand.split(/\s+/);
    const cmd = parts[0];
    const args = parts.slice(1);

    this.child = spawn(cmd, args, {
      stdio: ['pipe', 'pipe', 'inherit'],
    });

    this._isRunning = true;

    this.child.on('exit', () => {
      this._isRunning = false;
    });

    this.child.on('error', () => {
      this._isRunning = false;
    });

    // Read NDJSON lines from child stdout
    if (this.child.stdout) {
      this.childRl = createInterface({ input: this.child.stdout });
      this.childRl.on('line', (line) => {
        let outputLine = line;

        // Parse for interception / injection
        try {
          const msg = JSON.parse(line) as Record<string, unknown>;
          this.interceptor.onResponse(msg);

          // Inject local tool definitions into tools/list responses
          if (
            this.localTools &&
            msg['id'] != null &&
            this.pendingToolsListIds.has(msg['id'] as string | number)
          ) {
            this.pendingToolsListIds.delete(msg['id'] as string | number);
            const result = msg['result'] as Record<string, unknown> | undefined;
            if (result && typeof result === 'object') {
              const existing = Array.isArray(result['tools']) ? result['tools'] : [];
              const augmented = {
                ...msg,
                result: {
                  ...result,
                  tools: [...existing, ...LOCAL_TOOL_DEFINITIONS],
                },
              };
              outputLine = JSON.stringify(augmented);
            }
          }
        } catch {
          // Invalid JSON — forward anyway
        }

        for (const handler of this.outputHandlers) {
          handler(outputLine);
        }
      });
    }

    // Give the child a moment to start
    await new Promise<void>((resolve) => {
      if (!this.child) return resolve();

      // If the child has a pid, it's started
      if (this.child.pid !== undefined) {
        resolve();
        return;
      }

      this.child.on('spawn', () => resolve());
      this.child.on('error', () => resolve());
    });
  }

  /** Kill the child process and clean up. */
  async close(): Promise<void> {
    if (!this.child) return;

    this.childRl?.close();

    return new Promise<void>((resolve) => {
      if (!this.child || !this._isRunning) {
        this._isRunning = false;
        resolve();
        return;
      }

      this.child.on('exit', () => {
        this._isRunning = false;
        resolve();
      });

      this.child.kill('SIGTERM');

      // Force kill after 2s if SIGTERM doesn't work
      setTimeout(() => {
        if (this._isRunning && this.child) {
          this.child.kill('SIGKILL');
        }
      }, 2000);
    });
  }
}
